import React, { useState } from 'react';
import { motion } from 'framer-motion';

const BadgeApplicationPage: React.FC = () => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    productName: '',
    productDescription: '',
    ingredients: '',
    certifications: '',
    manufacturingProcess: '',
    website: '',
    amazonLink: '',
    upc: '',
    additionalInfo: '',
    agreeToTerms: false
  });
  
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
    
    // Clear error when field is edited
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    // Required fields
    if (!formData.companyName.trim()) errors.companyName = 'Company name is required';
    if (!formData.contactName.trim()) errors.contactName = 'Contact name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!formData.productName.trim()) errors.productName = 'Product name is required';
    if (!formData.ingredients.trim()) errors.ingredients = 'Ingredients are required';
    
    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Website validation
    if (formData.website && !/^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?$/.test(formData.website)) {
      errors.website = 'Please enter a valid website URL';
    }
    
    // Terms agreement
    if (!formData.agreeToTerms) {
      errors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    return errors;
  };
  
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 1500);
  };
  
  if (isSubmitted) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-md p-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-green-100 rounded-full mb-6">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-brand-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Application Submitted Successfully!</h1>
              
              <p className="text-gray-600 mb-6">
                Thank you for applying for a GoodCart badge. Our team will review your application and get back to you within 5-7 business days.
              </p>
              
              <div className="bg-gray-50 p-4 rounded-lg text-left mb-6">
                <h2 className="text-lg font-medium text-gray-900 mb-2">What happens next?</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Our team will review your application and product information</li>
                  <li>We may request additional information or product samples</li>
                  <li>Your product will be evaluated against our quality criteria</li>
                  <li>If approved, you'll receive your badge code and integration options</li>
                </ol>
              </div>
              
              <div className="flex justify-center">
                <a href="/" className="btn btn-primary">
                  Return to Home
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Apply for GoodCart Badge</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Submit your product for review to be considered for a GoodCart badge. Products that meet our highest standards may qualify for the GoodCart Perfect 100% badge.
            </p>
          </motion.div>
          
          {/* Application Form */}
          <motion.div 
            className="bg-white rounded-lg shadow-md overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="border-b border-gray-200 bg-gray-50 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">Product Review Application</h2>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6">
              {/* Company Information */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Company Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="companyName"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${formErrors.companyName ? 'border-red-500' : 'border-gray-300'} py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500`}
                    />
                    {formErrors.companyName && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.companyName}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                      Company Website
                    </label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      placeholder="https://example.com"
                      className={`w-full rounded-md border ${formErrors.website ? 'border-red-500' : 'border-gray-300'} py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500`}
                    />
                    {formErrors.website && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.website}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="contactName"
                      name="contactName"
                      value={formData.contactName}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${formErrors.contactName ? 'border-red-500' : 'border-gray-300'} py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500`}
                    />
                    {formErrors.contactName && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.contactName}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${formErrors.email ? 'border-red-500' : 'border-gray-300'} py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500`}
                    />
                    {formErrors.email && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                    />
                  </div>
                </div>
              </div>
              
              {/* Product Information */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Product Information</h3>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-1">
                      Product Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="productName"
                      name="productName"
                      value={formData.productName}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${formErrors.productName ? 'border-red-500' : 'border-gray-300'} py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500`}
                    />
                    {formErrors.productName && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.productName}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="productDescription" className="block text-sm font-medium text-gray-700 mb-1">
                      Product Description
                    </label>
                    <textarea
                      id="productDescription"
                      name="productDescription"
                      rows={3}
                      value={formData.productDescription}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                    />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="amazonLink" className="block text-sm font-medium text-gray-700 mb-1">
                        Product Link
                      </label>
                      <input
                        type="text"
                        id="amazonLink"
                        name="amazonLink"
                        value={formData.amazonLink}
                        onChange={handleChange}
                        placeholder="https://amazon.com/dp/..."
                        className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="upc" className="block text-sm font-medium text-gray-700 mb-1">
                        UPC/EAN Code
                      </label>
                      <input
                        type="text"
                        id="upc"
                        name="upc"
                        value={formData.upc}
                        onChange={handleChange}
                        className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Quality Information */}
              <div className="mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Quality Information</h3>
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <label htmlFor="ingredients" className="block text-sm font-medium text-gray-700 mb-1">
                      Ingredients <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="ingredients"
                      name="ingredients"
                      rows={3}
                      value={formData.ingredients}
                      onChange={handleChange}
                      className={`w-full rounded-md border ${formErrors.ingredients ? 'border-red-500' : 'border-gray-300'} py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500`}
                      placeholder="List all ingredients in the product"
                    />
                    {formErrors.ingredients && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.ingredients}</p>
                    )}
                  </div>
                  
                  <div>
                    <label htmlFor="certifications" className="block text-sm font-medium text-gray-700 mb-1">
                      Certifications
                    </label>
                    <textarea
                      id="certifications"
                      name="certifications"
                      rows={2}
                      value={formData.certifications}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                      placeholder="e.g., Organic, Non-GMO, Fair Trade, etc."
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="manufacturingProcess" className="block text-sm font-medium text-gray-700 mb-1">
                      Manufacturing Process
                    </label>
                    <textarea
                      id="manufacturingProcess"
                      name="manufacturingProcess"
                      rows={3}
                      value={formData.manufacturingProcess}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                      placeholder="Describe how the product is manufactured, including sustainability practices"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="additionalInfo" className="block text-sm font-medium text-gray-700 mb-1">
                      Additional Information
                    </label>
                    <textarea
                      id="additionalInfo"
                      name="additionalInfo"
                      rows={3}
                      value={formData.additionalInfo}
                      onChange={handleChange}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                      placeholder="Any other information that may be relevant to your application"
                    />
                  </div>
                </div>
              </div>
              
              {/* Terms and Submission */}
              <div className="mb-6">
                <div className={`flex items-start ${formErrors.agreeToTerms ? 'border-l-4 border-red-500 pl-3' : ''}`}>
                  <div className="flex items-center h-5">
                    <input
                      id="agreeToTerms"
                      name="agreeToTerms"
                      type="checkbox"
                      checked={formData.agreeToTerms}
                      onChange={handleCheckboxChange}
                      className="h-4 w-4 text-brand-green-600 focus:ring-brand-green-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="agreeToTerms" className="font-medium text-gray-700">
                      I agree to the terms and conditions <span className="text-red-500">*</span>
                    </label>
                    <p className="text-gray-500">
                      By submitting this application, you agree to allow GoodCart to review your product and potentially display it on our platform. You also confirm that all information provided is accurate and complete.
                    </p>
                    {formErrors.agreeToTerms && (
                      <p className="mt-1 text-sm text-red-500">{formErrors.agreeToTerms}</p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn btn-primary"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Submitting...
                    </>
                  ) : 'Submit Application'}
                </button>
              </div>
            </form>
          </motion.div>
          
          {/* Process Information */}
          <motion.div 
            className="mt-8 bg-white rounded-lg shadow-md p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Application Process</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-3">
                  <div className="bg-brand-green-100 rounded-full w-8 h-8 flex items-center justify-center text-brand-green-600 font-semibold mr-2">1</div>
                  <h3 className="font-medium text-gray-900">Submit Application</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Complete the application form with detailed information about your product and company.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-3">
                  <div className="bg-brand-green-100 rounded-full w-8 h-8 flex items-center justify-center text-brand-green-600 font-semibold mr-2">2</div>
                  <h3 className="font-medium text-gray-900">Product Review</h3>
                </div>
                <p className="text-sm text-gray-600">
                  Our team will review your product against our quality criteria, which includes ingredients, manufacturing processes, and certifications.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-3">
                  <div className="bg-brand-green-100 rounded-full w-8 h-8 flex items-center justify-center text-brand-green-600 font-semibold mr-2">3</div>
                  <h3 className="font-medium text-gray-900">Badge Issuance</h3>
                </div>
                <p className="text-sm text-gray-600">
                  If approved, you'll receive a badge based on your product's score. Products with a 100% score qualify for the GoodCart Perfect badge.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BadgeApplicationPage;